import {
  Button,
  Flex,
  Grid,
  Heading,
  RadioButton,
  RadioButtonGroup,
  SimpleGrid,
  Stack,
} from '@hover/blueprint';
import React, { useContext } from 'react';
import { Controller } from 'react-hook-form';
import { useHistory } from 'react-router';
import { type InferType } from 'yup';

import { getUserSession } from 'src/modules/homeownerApp/hooks';
import { OnboardingFormFooter } from 'src/modules/onboarding/components/OnboardingFormFooter';
import { makeOrgUsageValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Form as SharedForm } from 'src/shared/components/forms';
import {
  OnboardingSignupTypes,
  useCreateOrgSurveyQualifierMutation,
  useOnboardingCreateOrgForUserMutation,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import {
  Page,
  accountCreated,
  buttonPressed,
  pageViewed,
} from 'src/shared/telemetry';

import { OnboardingContext } from './OnboardingContext';

export type UserOrgFormData = InferType<
  ReturnType<typeof makeOrgUsageValidationSchema>
>;

interface OrgFormData {
  orgUsage: {
    min?: number;
    max?: number | undefined;
  };
}

interface UserOrgUsageFormFieldsProps {
  skip: () => void;
}

const UserOrgUsageFormFields = ({ skip }: UserOrgUsageFormFieldsProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const isMobile = useIsMobileSize();

  const [contextState] = useContext(OnboardingContext);

  interface OrgUsageOption {
    min: number;
    max: number | undefined;
    str: string;
  }

  const options: OrgUsageOption[] = [
    { min: 1, max: 50, str: '1-50' },
    { min: 51, max: 99, str: '51-99' },
    { min: 100, max: 199, str: '100-199' },
    { min: 200, max: 399, str: '200-399' },
    { min: 400, max: 999, str: '400-999' },
    { min: 1000, max: undefined, str: '1000+' },
  ];

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="wrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      spacing={0}
      width={1}
    >
      <Flex flexDir="column" maxW={800}>
        <Heading size={{ base: 700, tablet: 800 }}>
          {contextState.orgType === OnboardingSignupTypes.Insurance
            ? t('onboarding.aboutHowManyInspections')
            : t('onboarding.aboutHowManyInstalls')}
        </Heading>
      </Flex>
      <Flex
        alignItems="center"
        flex={{ base: 1, tablet: 0.4 }}
        flexDir="column"
      >
        <Controller
          name="orgUsage"
          render={props => (
            <RadioButtonGroup
              onChange={val => {
                props.onChange({
                  min: options[parseInt(val, 10)].min,
                  max: options[parseInt(val, 10)].max,
                });
              }}
              width={1}
            >
              <SimpleGrid
                columns={1}
                height="fit-content"
                spacingX={500}
                spacingY={300}
                width={1}
              >
                {options.map((option, index) => (
                  <RadioButton
                    borderRadius="20px"
                    size="large"
                    value={index.toString()}
                  >
                    {option.str}
                  </RadioButton>
                ))}
              </SimpleGrid>
            </RadioButtonGroup>
          )}
        />
        {!isMobile && (
          <Button
            mt={500}
            onClick={() => {
              buttonPressed({
                feature: 'onboarding',
                page_or_screen_name: 'Onboarding-projects',
                button_text: 'Skip',
              });
              skip();
            }}
            variant="ghost"
            width="fit-content"
          >
            {t('onboarding.skip')}
          </Button>
        )}
      </Flex>
    </Stack>
  );
};

export const UserOrgUsage = () => {
  const history = useHistory();
  const userSession = getUserSession();
  const t = useTranslate(TranslationNamespace.SHARED);

  const [createOrgSurveyQualifier] = useCreateOrgSurveyQualifierMutation();
  const [onboardingCreateOrgForUser] = useOnboardingCreateOrgForUserMutation();

  const [contextState] = useContext(OnboardingContext);

  const validationSchema = makeOrgUsageValidationSchema();
  const initialValues = {};

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-projects_input',
      feature: 'onboarding',
    });
  });

  const navigateToInviteTeam = () => {
    history.push(`${Routes.SIGN_UP_ONBOARDING.path}/invite`);
  };

  const { orgType, orgName } = contextState;

  const createOrg = () => {
    void onboardingCreateOrgForUser({
      variables: {
        createOrgForUserArgs: {
          org: {
            name: orgName,
          },
          signupType: orgType as OnboardingSignupTypes,
        },
      },
      onCompleted: () =>
        // Only track this on successful org creation, errors if user has already created org
        accountCreated({
          button_or_link: 'button',
          button_or_link_text: t('onboarding.next'),
          page_or_screen_name: Page.SIGN_UP_ONBOARDING,
          primary_cta: true,
          platform_type: 'web',
          session_id: userSession?.sessionId,
        }),
    });
  };

  const onSubmit = (formData: OrgFormData) => {
    createOrg();

    // Construct descriptions array
    const descriptions: string[] = structuredClone(
      contextState.orgDescriptions,
    );
    if (contextState.customDescription)
      descriptions.push(contextState.customDescription);

    let orgSurveyQualifierCreateArgs = {
      source: 'Web signup new onboarding flow',
      industryType:
        contextState.orgType === OnboardingSignupTypes.Insurance
          ? 'Insurance'
          : 'Pro',
      businessDescription: descriptions,
    };

    if (orgType === OnboardingSignupTypes.Insurance) {
      orgSurveyQualifierCreateArgs = {
        ...orgSurveyQualifierCreateArgs,
        ...{
          jobEstimateLowerBound: formData.orgUsage?.min,
          jobEstimateUpperBound: formData.orgUsage?.max,
        },
      };
    } else {
      orgSurveyQualifierCreateArgs = {
        ...orgSurveyQualifierCreateArgs,
        ...{
          jobInstallLowerBound: formData.orgUsage?.min,
          jobInstallUpperBound: formData.orgUsage?.max,
        },
      };
    }

    void createOrgSurveyQualifier({
      variables: {
        orgSurveyQualifierCreateArgs,
      },
    });

    navigateToInviteTeam();
  };

  const onSkip = () => {
    createOrg();
    navigateToInviteTeam();
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserOrgFormData>
        display="flex"
        height="100%"
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <UserOrgUsageFormFields skip={onSkip} />
        <OnboardingFormFooter hideForMobile={false} />
      </SharedForm>
    </Grid>
  );
};
